import React from "react";
import { Building2, MapPin, Phone, Footprints, Clock, ExternalLink } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { useContent } from "@/context/ContentContext";
import { mediaUrl } from "@/lib/api";
import { SectionLayout, SectionIntro } from "@/components/SectionLayout";

export const Hotels = ({ data }) => {
  const { L, t } = useLang();
  const { content } = useContent();
  const notice = content?.hotelsNotice;
  const hotels = Array.isArray(data) ? data : [];

  return (
    <SectionLayout id="hotels" icon={Building2}>
      <div className="space-y-6">
        {hotels.map((h) => (
          <div key={h.id} className="maqam-card overflow-hidden rounded-3xl" data-testid={`hotel-${h.id}`}>
            <div className="relative h-52 w-full overflow-hidden">
              <img src={mediaUrl(h.image)} alt={L(h.name)} className="h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <span className="absolute left-4 top-4 rounded-full bg-[#D4AF37] px-3 py-1 text-xs font-semibold text-[#0A1128]">
                {L(h.city)}
              </span>
              <h3 className="absolute bottom-4 left-4 right-4 font-display text-2xl font-medium text-white leading-tight">
                {L(h.name)}
              </h3>
            </div>
            <div className="p-5 sm:p-6">
              {L(h.address) && (
                <div className="flex items-start gap-2 text-sm text-[#0A1128]/75">
                  <MapPin size={16} className="mt-0.5 shrink-0 text-[#D4AF37]" />
                  <span>{L(h.address)}</span>
                </div>
              )}

              <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Info icon={Footprints} label={t("walkingDistance")} value={L(h.walkingDistance)} />
                <Info icon={Clock} label={t("checkInInfo")} value={L(h.checkIn)} />
              </div>

              {Array.isArray(h.amenities) && h.amenities.length > 0 && (
                <div className="mt-4">
                  <p className="mb-2 text-[11px] uppercase tracking-[0.16em] text-[#64748B]">{t("amenities")}</p>
                  <div className="flex flex-wrap gap-2">
                    {h.amenities.map((a, i) => (
                      <span key={i} className="rounded-full bg-[#0A192F]/6 px-3 py-1 text-xs text-[#0A192F]">
                        {L(a)}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-5 flex flex-wrap gap-2">
                {h.mapsUrl && (
                  <a
                    href={h.mapsUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-full bg-[#0A192F] px-4 py-2 text-sm font-medium text-white transition-transform hover:-translate-y-0.5"
                  >
                    <MapPin size={15} /> {t("viewOnMaps")} <ExternalLink size={13} />
                  </a>
                )}
                {h.phone && (
                  <a
                    href={`tel:${h.phone}`}
                    className="inline-flex items-center gap-1.5 rounded-full border border-[#0A192F]/15 px-4 py-2 text-sm font-medium text-[#0A192F] transition-colors hover:bg-[#0A192F]/6"
                  >
                    <Phone size={15} /> {t("call")}
                  </a>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      {notice && (
        <div className="mt-6 rounded-2xl border border-[#D4AF37]/30 bg-[#FBF3D8]/60 px-4 py-3 text-sm leading-relaxed text-[#0A1128]/80" data-testid="hotels-notice">
          <p className="font-semibold text-[#0A192F]">{L(notice.title)}</p>
          <p className="mt-1">{L(notice.body)}</p>
        </div>
      )}
    </SectionLayout>
  );
};

const Info = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3 rounded-2xl bg-[#F1F5F9]/70 p-3">
    <span className="grid h-9 w-9 place-items-center rounded-full bg-white text-[#0A192F]">
      <Icon size={16} strokeWidth={1.75} />
    </span>
    <div className="min-w-0">
      <p className="text-[10px] uppercase tracking-[0.14em] text-[#64748B]">{label}</p>
      <p className="text-sm font-medium leading-snug text-[#0A192F]">{value}</p>
    </div>
  </div>
);
